<template>
   <Head>
      <title>{{ $t("meta.error.title") }}</title>
      <meta name="description" :content="$t('meta.error.content')" />
   </Head>

   <div class="text-regular flex min-h-screen w-full items-center justify-center bg-brand-light text-brand-dark">
      <div class="max-w-md text-center">
         <h1 class="h2 mb-12 px-4 text-2xl" data-cy="error-page-title">{{ error.title }}</h1>

         <div v-if="error.paragraphs" class="pb-6" data-cy="error-page-description">
            <p v-for="paragraph in error.paragraphs" :key="paragraph" class="mb-2">{{ paragraph }}</p>
         </div>
         <ul v-if="error.list" class="pb-6">
            <li v-for="item in error.list" :key="item">
               {{ item }}
            </li>
         </ul>

         <LinkButton v-for="link in error.links" :key="link.id" :url="link.url" class="mr-4">
            {{ link.text }}
         </LinkButton>
         <SupportButton
            v-if="error.supportButton"
            class="inline-block"
            styleType="outlined"
            :text="$t('system.contact_us')"
         />
      </div>
   </div>
</template>

<script setup lang="ts">
import { Head } from "@unhead/vue/components";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { FlowAccessErrorCodeStatus } from "@/middleware/studyFlowAccess";
import { useAuthStore } from "@/stores/authStore";

interface Link {
   id: string;
   text: string;
   url: string;
}
interface ErrorPageInfo {
   title: string;
   paragraphs?: string[];
   list?: string[];
   links: Link[];
   supportButton?: boolean;
}

const { t: $t } = useI18n();
const authStore = useAuthStore();

const props = defineProps({
   errorType: { type: String, default: "unknown_error" },
   detail: { type: Object, default: null },
});

// computed
// -----------------------------------------
const homeLink = computed(() => {
   const link: Link = { id: "home", text: $t("redirects.go_to_home"), url: "/" };
   if (authStore.authTokenExists) link.url = "/home";

   return link;
});

const error = computed<ErrorPageInfo>((): ErrorPageInfo => {
   if (props.errorType === "bad_request") {
      return {
         title: $t("validation.error_400"),
         links: [homeLink.value, { id: "reload", text: $t("system.reload"), url: "javascript:location.reload();" }],
      };
   } else if (props.errorType === FlowAccessErrorCodeStatus.FLOW_LOCALE_NOT_FOUND) {
      return {
         title: $t("validation.flow.flow_locale_not_found.paragraph_1"),
         list: [
            $t("validation.flow.flow_locale_not_found.list_item_1"),
            $t("validation.flow.flow_locale_not_found.list_item_2"),
         ],
         links: [homeLink.value],
      };
   } else if (props.errorType === FlowAccessErrorCodeStatus.STUDY_PARTICIPATION_INACTIVE) {
      return {
         title: $t("validation.flow.flow_inactive_user.heading"),
         paragraphs: [
            $t("validation.flow.flow_inactive_user.paragraph_1"),
            $t("validation.flow.flow_inactive_user.paragraph_2"),
         ],
         links: [homeLink.value],
         supportButton: true,
      };
   } else if (props.errorType === FlowAccessErrorCodeStatus.UNAUTHORIZED_FLOW_USER) {
      return {
         title: $t("validation.flow.unauthorized_flow_user.paragraph_1"),
         paragraphs: [
            $t("validation.flow.unauthorized_flow_user.paragraph_2"),
            $t("validation.flow.unauthorized_flow_user.paragraph_3"),
         ],
         links: [homeLink.value],
      };
   } else if (props.errorType === FlowAccessErrorCodeStatus.FLOW_NOT_FOUND) {
      return {
         title: $t(`validation.error_generic_msg`),
         paragraphs: [$t("survey.errors.survey_fetch_error")],
         links: [homeLink.value],
      };
   } else if (props.errorType === "page_not_found") {
      return {
         title: $t("validation.error_404"),
         links: [homeLink.value],
      };
   }
   // unknown error type
   else {
      return { title: $t(`validation.error_generic_msg`), links: [homeLink.value] };
   }
});
</script>
